<script setup lang="ts">
import { computed } from 'vue'
import { useTableStore } from '@/stores/tableStore.ts'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import { useMapStore } from '@/stores/mapStore.ts'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import ActionBarSelect from '@/components/Interface/DataTable/ActionBar/ActionBarSelect.vue'
import PlayerName from '@/components/Objects/PlayerName.vue'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import type Fleet from '@/types/Fleet.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const tableStore = useTableStore();
const mapStore = useMapStore();
const playerRelationsStore = usePlayerRelationStore();
const mapCoordinateStore = useMapCoordinateStore();
const gameStore = useGameStore();

// Enrich fleet data with player names for sorting
const enrichedFleets = computed(() => {
  const enriched = new Map<number, Fleet & { playerName: string }>();

  for (const [id, fleet] of mapCoordinateStore.fleets) {
    enriched.set(id, {
      ...fleet,
      playerName: playerRelationsStore.getNameByPlayerId(fleet.playerId)
    });
  }

  return enriched;
});

//Load filtered fleet list
const filters = (fleets: Fleet[]) => {
  return fleets
    .filter(fleet => fleet.playerId !== gameStore.player?.id)
    .filter((fleet) => {
      // only show enemy fleets
      if(playerRelationsStore.isEnemy(fleet.playerId)) {
        return true;
      }
    })
    .filter((fleet) => {
      if(tableStore.mapCoordsEnemyFleets.filters.selectedPlayer === '') {
        return true;
      }
      if(Number(tableStore.mapCoordsEnemyFleets.filters.selectedPlayer) === fleet.playerId) {
        return true;
      }
    });
}

const onClick = (id:number) => {
  const data = mapCoordinateStore.fleets.get(id);
  if(data) {
    mapStore.focusMap(data.xPos, data.yPos);
  }
};

</script>false

<template>
  <FerionDataTable
    :data="enrichedFleets"
    tableStoreName="mapCoordsEnemyFleets"
    :selector="false"
    :filters="filters"
    :heightLimit="'h-[calc(100vh-9rem)]'"
    :click="onClick"
  >
    <!--- ActionBar --->
    <template #actionBar>
      <ActionBarFiller class="mr-1 rounded">
        <div class="py-3">
          &nbsp;
        </div>
      </ActionBarFiller>
      <ActionBarSelect :name="t('map.sidebar.fleets.player')" width="6" v-model="tableStore.mapCoordsEnemyFleets.filters.selectedPlayer">
        <option value="" selected>{{ t('map.sidebar.fleets.all') }}</option>
        <template v-for="player in playerRelationsStore.relations.values()">
          <option v-if="playerRelationsStore.isEnemy(player.playerId)" :value="player.playerId" :key="player.playerId">{{ player.name }}</option>
        </template>
      </ActionBarSelect>
    </template>
    <!--- Headers --->
    <template #columns="{ sort, sortColumn, sortDirection }">
      <HeaderTextFlex min-width="12" column="playerName" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" class="rounded-tl">
        <span class="pl-2">{{ t('map.sidebar.fleets.player') }}</span>
      </HeaderTextFlex>
      <HeaderFixedWidthText width="6" column="eta" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" align="center" :title="t('map.sidebar.fleets.distanceTooltip')">
        {{ t('map.sidebar.fleets.distance') }}
      </HeaderFixedWidthText>
      <HeaderFixedWidthText width="6" column="ships" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" align="center" class="rounded-tr">
        {{ t('map.sidebar.fleets.ships') }}
      </HeaderFixedWidthText>
    </template>
    <!--- Rows --->
    <template #rows="{ item }" >
      <CellFlexWidth min-width="12">
        <div class="flex flex-col pl-2">
          <div><PlayerName :player-id="item.playerId"/></div>
        </div>
      </CellFlexWidth>
      <CellFixedWidth width="6" align="center">
        {{ item.eta>0 && item.eta !== Infinity ? (item.eta >= 500 ? '500+' : item.eta) : '-' }}
      </CellFixedWidth>
      <CellFixedWidth width="6" align="center">
        {{ item.ships }}
      </CellFixedWidth>
    </template>

  </FerionDataTable>
</template>

<style scoped lang="scss">

</style>